import type { ReactNode } from 'react';
import './walletButton.css';

export type WalletReadyState = 'loading' | 'ready' | 'unavailable';

interface WalletButtonShellProps {
  /**
   * Brand-styled placeholder painted from t=0 so the player sees an
   * Apple/Google/PayPal-looking button immediately. Hidden once the
   * real PSP-rendered button is ready.
   */
  placeholder: ReactNode;
  /**
   * The real PSP-managed button (Adyen drop-in, Stripe ECE,
   * BraintreePayPalButtons). Mounted from the start so its SDK boots
   * in parallel with the placeholder being visible. Becomes
   * interactive only when `state === 'ready'`.
   */
  children: ReactNode;
  /**
   * Tri-state driven by the PSP's `onReady`/`onInit` callback:
   *   - `loading`     placeholder + loader overlay; clicks blocked.
   *   - `ready`       loader fades, placeholder hides, real button
   *                   reveals with a one-shot pulse animation.
   *   - `unavailable` the wrapper smoothly collapses to zero height
   *                   (~280ms) so siblings below glide up rather
   *                   than jumping. The wrapper stays in the DOM
   *                   (otherwise the transition couldn't play); it's
   *                   `aria-hidden` and `pointer-events: none` from
   *                   that point on.
   */
  state: WalletReadyState;
  /**
   * Forwarded to the wrapper so wallet-specific E2E selectors keep
   * working (e.g. `wallet-btn-apple`, `wallet-btn-google`).
   */
  testId?: string;
  /**
   * Keep using the placeholder as the final visual button after the PSP is
   * ready, while the real PSP button stays active underneath as the click
   * target. Use for express wallets whose provider iframes do not match the
   * Figma dimensions/radius exactly.
   */
  usePlaceholderAsVisual?: boolean;
}

/**
 * Brand-correct loading shell for express wallet buttons.
 *
 * Three layers stacked via CSS Grid (no `position:absolute` — the
 * wrapper keeps its natural button height so layout doesn't shift
 * when the real button paints):
 *
 *   ┌─────────────────────────────────────┐
 *   │ overlay  (semi-opaque + spinner)    │  z=3, fades out on ready
 *   │ real     (PSP-managed button)       │  z=2, fades in on ready
 *   │ placeholder (brand-correct visual)  │  z=1, fades out on ready
 *   └─────────────────────────────────────┘
 *
 * The overlay enforces "not clickable while loading" — it has
 * `pointer-events: auto`, sitting between the player and both
 * underlying layers. When ready, the overlay's pointer-events flip
 * to `none` in the same frame as the real button's opacity hits 1,
 * so the player can never click an invisible button.
 *
 * The pulse animation on `.is-ready` runs once (520ms) and serves as
 * the explicit "I'm clickable now" cue.
 */
export const WalletButtonShell = ({
  placeholder,
  children,
  state,
  testId,
  usePlaceholderAsVisual = false,
}: WalletButtonShellProps): JSX.Element => {
  const isReady = state === 'ready';
  const isDisabled = state === 'unavailable';
  const className = [
    'wallet-btn',
    isReady ? 'is-ready' : '',
    isDisabled ? 'is-disabled' : '',
    usePlaceholderAsVisual ? 'is-visual-proxy' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={className}
      data-testid={testId}
      data-wallet-state={state}
      aria-busy={state === 'loading' || undefined}
      aria-hidden={isDisabled || undefined}
    >
      <div className="wallet-btn__placeholder">{placeholder}</div>
      <div className="wallet-btn__real">{children}</div>
      <div className="wallet-btn__overlay" aria-hidden={isReady || isDisabled ? 'true' : undefined}>
        <span className="wallet-btn__spinner" role="status" aria-label="Loading payment button" />
      </div>
    </div>
  );
};
